import { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Building2, MapPin, Globe, Instagram, Music2, FileText, Sparkles, Trash2, Plus } from "lucide-react";

const SECTIONS = [
  {
    title: "Identité",
    icon: Building2,
    fields: [
      { k: "business_name", label: "Nom du commerce", required: true, placeholder: "Le Petit Bistrot" },
      { k: "industry", label: "Secteur", placeholder: "Restaurant, salon, café…" },
    ],
  },
  {
    title: "Localisation",
    icon: MapPin,
    fields: [
      { k: "address", label: "Adresse complète", placeholder: "12 rue Saint-Honoré, 75001 Paris" },
    ],
  },
  {
    title: "Présence en ligne",
    icon: Globe,
    fields: [
      { k: "website", label: "Site web", placeholder: "https://…" },
      { k: "google_business", label: "Fiche Google Business", placeholder: "URL de votre fiche" },
    ],
  },
  {
    title: "Réseaux sociaux",
    icon: Instagram,
    fields: [
      { k: "instagram", label: "Instagram", placeholder: "@votrecompte" },
      { k: "tiktok", label: "TikTok", placeholder: "@votrecompte" },
    ],
  },
];

const ALL_FIELDS = ["business_name","industry","address","website","google_business","instagram","tiktok","description"];

export default function BusinessProfile() {
  const [businesses, setBusinesses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await apiClient.get("/business");
    setBusinesses(r.data);
    if (r.data.length && !editing) { setEditing(r.data[0]); setForm(r.data[0]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const newOne = () => { setEditing({ business_id: null }); setForm({}); };
  const completion = Math.round((ALL_FIELDS.filter(f => form[f]).length / ALL_FIELDS.length) * 100);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing?.business_id) {
        await apiClient.put(`/business/${editing.business_id}`, form);
        toast.success("Profil mis à jour");
      } else {
        const r = await apiClient.post("/business", form);
        setEditing(r.data);
        toast.success("Profil créé");
      }
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing?.business_id) return;
    if (!confirm("Supprimer ce profil ?")) return;
    await apiClient.delete(`/business/${editing.business_id}`);
    setEditing(null); setForm({}); load();
  };

  return (
    <div className="space-y-8" data-testid="business-profile-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Profil business</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
            Construisez votre <span className="font-serif-italic font-normal">empreinte locale</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Plus votre profil est complet, plus vous êtes recommandé — sur Google, dans les avis et auprès de vos voisins.
          </p>
        </div>
        {businesses.length > 0 && (
          <Button variant="outline" className="rounded-full" onClick={newOne} data-testid="new-business-btn">
            <Plus className="h-4 w-4 mr-1.5" />Nouveau profil
          </Button>
        )}
      </div>

      {/* Completion progress card */}
      {(editing || businesses.length === 0) && (
        <div className="relative overflow-hidden border border-border rounded-2xl p-6 bg-gradient-to-br from-primary/5 to-violet-500/5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Profil complété</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-black font-display text-gradient">{completion}%</span>
                <span className="text-sm text-muted-foreground">{ALL_FIELDS.filter(f => form[f]).length} / {ALL_FIELDS.length} champs</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full transition-all duration-700" style={{ width: `${completion}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {completion < 50 && "Continuez : un profil complet vaut un client en plus chaque semaine."}
                {completion >= 50 && completion < 100 && "Presque parfait. Encore un effort pour maximiser votre visibilité."}
                {completion === 100 && "Parfait. Votre fiche est optimisée — on pousse votre commerce en ligne."}
              </p>
            </div>
          </div>
        </div>
      )}

      {businesses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {businesses.map(b => (
            <Button key={b.business_id} variant={editing?.business_id === b.business_id ? "default" : "outline"} size="sm"
              className="rounded-full" onClick={() => { setEditing(b); setForm(b); }} data-testid={`select-biz-${b.business_id}`}>
              <Building2 className="h-3.5 w-3.5 mr-1.5" />{b.business_name}
            </Button>
          ))}
        </div>
      )}

      <form onSubmit={save} className="space-y-6" data-testid="business-form">
        {SECTIONS.map(section => (
          <div key={section.title} className="border border-border rounded-2xl p-6 bg-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
                <section.icon className="h-4 w-4 text-primary" />
              </div>
              <h3 className="font-bold tracking-tight">{section.title}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map(f => (
                <div key={f.k} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold">{f.label}{f.required && " *"}</Label>
                  <Input required={f.required} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    placeholder={f.placeholder} className="rounded-md h-11" data-testid={`field-${f.k}`} />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="border border-border rounded-2xl p-6 bg-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-bold tracking-tight">Description</h3>
              <p className="text-xs text-muted-foreground mt-0.5">100-200 mots, mots-clés locaux, ambiance et spécialités.</p>
            </div>
          </div>
          <Textarea rows={6} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Décrivez votre activité, vos spécialités, votre ambiance, vos valeurs…"
            className="rounded-md" data-testid="field-description" />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          {editing?.business_id && (
            <Button type="button" variant="outline" className="rounded-full text-destructive hover:bg-destructive/10" onClick={remove} data-testid="delete-business-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />Supprimer
            </Button>
          )}
          <Button disabled={saving} className="rounded-full btn-glow" data-testid="save-business-btn">
            <Sparkles className="h-4 w-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
